import type { InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorCircleIcon, InfoCircleIcon } from './icons'
import { Span, Text } from './typography'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  helpText?: string
  /** true = visual error state only; string = error state + message below */
  error?: boolean | string
  /** Text shown in a DaisyUI tooltip on the ⓘ icon next to the label */
  tooltip?: string
  /** Appends "(Optional)" in neutral-300 to the label */
  optional?: boolean
  /** Trailing icon inside the input box (replaced by the error icon when error is set) */
  icon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helpText,
      error,
      tooltip,
      optional,
      icon,
      id,
      disabled,
      className = '',
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const hasError = !!error
    const errorMessage = typeof error === 'string' ? error : undefined
    const displayedHelp = errorMessage ?? helpText

    return (
      <div className="flex w-full flex-col gap-3">
        {label && (
          <div className="flex items-center gap-1">
            <Text as="label" htmlFor={id} className="text-neutral-900">
              {label}
              {optional && (
                <Text as="span" className="text-neutral-300">
                  {` (${t('common.optional')})`}
                </Text>
              )}
            </Text>
            {tooltip && (
              <div className="tooltip" data-tip={tooltip}>
                <InfoCircleIcon className="text-indigo-base h-4 w-4" />
              </div>
            )}
          </div>
        )}
        <div className="relative w-full">
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            className={[
              'tracking-base h-12 w-full rounded-lg border px-4 py-3 font-sans text-base transition outline-none',
              'placeholder:text-neutral-300',
              hasError
                ? 'border-error-base bg-error-50'
                : 'border-neutral-300 bg-white hover:not-disabled:border-indigo-600 focus:border-indigo-300 focus:bg-indigo-50',
              'disabled:cursor-not-allowed disabled:bg-neutral-50',
              (hasError || icon) && 'pr-10',
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            {...props}
          />
          {(hasError || icon) && (
            <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
              {hasError ? (
                <ErrorCircleIcon className="text-error-50 fill-error-base h-6 w-6 stroke-2" />
              ) : (
                icon
              )}
            </div>
          )}
        </div>
        {displayedHelp && (
          <Span
            data-testid={id ? `${id}-error` : undefined}
            className={`${hasError ? 'text-error-base' : 'text-neutral-900'}`}
          >
            {displayedHelp}
          </Span>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'
